import { useEffect, useState } from "react";
import { Check, X, Trash2, Plus, Save, Star, Edit3, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/lib/content";

const COUNTRIES = ["Казахстан", "Узбекистан", "Кыргызстан", "Таджикистан", "Туркменистан", "Россия", "Беларусь", "Украина", "Грузия", "Армения", "Азербайджан"];

const empty = { name: "", country: "Казахстан", role: "", rating: 5, text: "", photo: "", phone: "", email: "", status: "approved", created_at: new Date().toISOString().slice(0, 10) };

const StatusBadge = ({ status }) => {
  const map = {
    pending: { cls: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30", label: "На рассмотрении" },
    approved: { cls: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30", label: "Одобрен" },
    rejected: { cls: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30", label: "Отклонён" },
  };
  const s = map[status] || map.pending;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>{s.label}</span>;
};

const ReviewForm = ({ initial, onSubmit, onCancel, submitLabel }) => {
  const [f, setF] = useState(initial);
  return (
    <div className="space-y-3" data-testid="review-form-admin">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Имя" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="review-form-name" />
        <select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="review-form-country">
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <input value={f.role || ""} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Должность / работа" className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm" />
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Телефон (необязательно)" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="review-form-phone" />
        <input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email (необязательно)" className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="review-form-email" />
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map((n) => (
            <button key={n} type="button" onClick={() => setF({ ...f, rating: n })} className="p-1">
              <Star className={`size-5 ${n <= f.rating ? "fill-accent text-accent" : "text-muted-foreground/40"}`} />
            </button>
          ))}
        </div>
        <input type="date" value={(f.created_at || "").slice(0, 10)} onChange={(e) => setF({ ...f, created_at: new Date(e.target.value).toISOString() })} className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="review-form-date" />
      </div>
      <textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={4} placeholder="Текст отзыва..." className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm resize-none" data-testid="review-form-text" />
      <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm">
        <option value="approved">Одобрен (виден на сайте)</option>
        <option value="pending">На рассмотрении</option>
        <option value="rejected">Отклонён</option>
      </select>
      <div className="flex justify-end gap-2">
        {onCancel && <button onClick={onCancel} className="h-9 px-4 rounded-lg border border-border text-xs font-medium hover:bg-muted">Отмена</button>}
        <button onClick={() => onSubmit(f)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-foreground text-background text-xs font-semibold" data-testid="review-form-submit">
          <Save className="size-3.5" /> {submitLabel}
        </button>
      </div>
    </div>
  );
};

export default function AdminReviews() {
  const { refetch } = useContent();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reviews/admin");
    setItems(data.items);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    try { await api.put(`/reviews/${r.id}`, { status }); toast.success(`${status}`); await load(); refetch(); }
    catch { toast.error("Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Удалить отзыв от ${r.name}?`)) return;
    try { await api.delete(`/reviews/${r.id}`); toast.success("Удалено"); await load(); refetch(); }
    catch { toast.error("Failed"); }
  };
  const update = async (form) => {
    try {
      await api.put(`/reviews/${editingId}`, form);
      toast.success("Обновлено");
      setEditingId(null);
      await load(); refetch();
    } catch { toast.error("Failed"); }
  };
  const create = async (form) => {
    if (!form.name || !form.text) { toast.error("Заполните имя и текст"); return; }
    try {
      await api.post("/reviews/admin", form);
      toast.success("Создано");
      setAdding(false);
      await load(); refetch();
    } catch { toast.error("Failed"); }
  };

  const visible = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = {
    all: items.length,
    pending: items.filter((i) => i.status === "pending").length,
    approved: items.filter((i) => i.status === "approved").length,
    rejected: items.filter((i) => i.status === "rejected").length,
  };

  return (
    <div className="max-w-5xl w-full">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Отзывы</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Модерация отзывов</h1>
          <p className="text-sm text-muted-foreground mt-2">Одобряйте, редактируйте или пишите свои отзывы.</p>
        </div>
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-foreground text-background font-semibold text-sm" data-testid="review-add-btn">
          <Plus className="size-4" /> Написать отзыв
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="review-filters">
        {[
          { k: "all", label: "Все" },
          { k: "pending", label: "На рассмотрении", highlight: true },
          { k: "approved", label: "Одобрен" },
          { k: "rejected", label: "Отклонён" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === f.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"
            } ${f.highlight && counts.pending > 0 && filter !== f.k ? "border-accent/50 text-accent" : ""}`}
            data-testid={`filter-${f.k}`}
          >
            {f.label} <span className="opacity-60 ml-1">{counts[f.k]}</span>
          </button>
        ))}
      </div>

      {adding && (
        <div className="p-4 mb-6 rounded-2xl border border-accent/40 bg-card glow-accent" data-testid="review-add-form">
          <h3 className="font-heading font-semibold mb-3">New review</h3>
          <ReviewForm initial={empty} onSubmit={create} onCancel={() => setAdding(false)} submitLabel="Создать" />
        </div>
      )}

      <div className="space-y-3">
        {visible.length === 0 && <div className="py-12 text-center text-muted-foreground text-sm">Нет отзывов в этой категории.</div>}
        {visible.map((r) => (
          <div key={r.id} className="p-4 rounded-2xl border border-border bg-card" data-testid={`review-row-${r.id}`}>
            {editingId === r.id ? (
              <ReviewForm
                initial={r}
                onSubmit={update}
                onCancel={() => setEditingId(null)}
                submitLabel="Сохранить"
              />
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{r.name}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="text-xs text-muted-foreground">{r.country}{r.role ? ` · ${r.role}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`size-3.5 ${i < r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                    ))}
                    <span className="text-xs text-muted-foreground ml-2 font-mono">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-foreground/85 mb-4">{r.text}</p>
                {(r.phone || r.email) && (
                  <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-xl bg-muted/40 border border-border text-xs" data-testid={`review-contacts-${r.id}`}>
                    <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">Контакты:</span>
                    {r.phone && (
                      <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                        <Phone className="size-3" /> {r.phone}
                      </a>
                    )}
                    {r.email && (
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                        <Mail className="size-3" /> {r.email}
                      </a>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 justify-end">
                  {r.status !== "approved" && (
                    <button onClick={() => setStatus(r, "approved")} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-green-500/15 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-500/25" data-testid={`review-approve-${r.id}`}>
                      <Check className="size-3.5" /> Одобрить
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button onClick={() => setStatus(r, "rejected")} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-red-500/15 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-500/25" data-testid={`review-reject-${r.id}`}>
                      <X className="size-3.5" /> Отклонить
                    </button>
                  )}
                  <button onClick={() => setEditingId(r.id)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted" data-testid={`review-edit-${r.id}`}>
                    <Edit3 className="size-3.5" /> Редактировать
                  </button>
                  <button onClick={() => del(r)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-destructive/30 text-destructive text-xs font-medium hover:bg-destructive hover:text-destructive-foreground" data-testid={`review-delete-${r.id}`}>
                    <Trash2 className="size-3.5" /> Удалить
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
